
import { Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "next-themes";
import { HelmetProvider } from 'react-helmet-async';
import ErrorBoundary from "./components/ErrorBoundary";
import { AuthProvider } from "./contexts/AuthContext";
import { LoadingProvider } from "./contexts/LoadingContext";
import { MobileMenuProvider } from "./contexts/MobileMenuContext";
import Navbar from "./components/Navbar";
import MobileNavigation from "./components/MobileNavigation";
import ScrollToTop from "./components/ScrollToTop";
import MobileEnhancer from "./components/MobileEnhancer";
import "./App.css";

// Lazy load pages for better performance
const Index = lazy(() => import("./pages/Index"));
const PropertiesList = lazy(() => import("./pages/PropertiesList"));
const PropertyDetails = lazy(() => import("./pages/PropertyDetails"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const Contact = lazy(() => import("./pages/Contact"));
const SignUp = lazy(() => import("./pages/SignUp"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const CEO = lazy(() => import("./pages/CEO"));
const PropTalk = lazy(() => import("./pages/PropTalk"));
const Investments = lazy(() => import("./pages/Investments"));
const Account = lazy(() => import("./pages/Account"));
const Faq = lazy(() => import("./pages/Faq"));
const DownloadApp = lazy(() => import("./pages/DownloadApp"));
const AdminBlogUploader = lazy(() => import("./pages/AdminBlogUploader"));
const NotFound = lazy(() => import("./pages/NotFound"));

// New dedicated pages
const HowItWorksPage = lazy(() => import("./pages/HowItWorksPage"));
const PropertiesPage = lazy(() => import("./pages/PropertiesPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

// Loading component with enterprise styling
const LoadingSpinner = () => (
  <div className="min-h-screen bg-propmatch-dark flex items-center justify-center">
    <div className="flex flex-col items-center space-y-4">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-propmatch-blue"></div>
      <p className="text-white/70 text-sm">Loading...</p>
    </div>
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
          <QueryClientProvider client={queryClient}>
            <TooltipProvider>
              <Router>
                <LoadingProvider>
                  <AuthProvider>
                    <MobileMenuProvider>
                      <div className="min-h-screen bg-propmatch-dark text-white flex flex-col overflow-x-hidden">
                        <Navbar />
                        
                        {/* Main content with smaller padding to reduce black area */}
                        <main className="flex-1 pt-12 md:pt-14 pb-20 md:pb-6 relative">
                          <Suspense fallback={<LoadingSpinner />}>
                            <Routes>
                              <Route path="/" element={<Index />} />
                              <Route path="/how-it-works" element={<HowItWorksPage />} />
                              <Route path="/properties" element={<PropertiesPage />} />
                              <Route path="/about" element={<AboutPage />} />
                              <Route path="/property/:id" element={<PropertyDetails />} />
                              <Route path="/contact" element={<Contact />} />
                              <Route path="/signup" element={<SignUp />} />
                              <Route path="/blog" element={<Blog />} />
                              <Route path="/blog/:id" element={<BlogPost />} />
                              <Route path="/ceo" element={<CEO />} />
                              <Route path="/proptalk" element={<PropTalk />} />
                              <Route path="/investments" element={<Investments />} />
                              <Route path="/account" element={<Account />} />
                              <Route path="/faq" element={<Faq />} />
                              <Route path="/download" element={<DownloadApp />} />
                              <Route path="/admin/blog-uploader" element={<AdminBlogUploader />} />
                              <Route path="*" element={<NotFound />} />
                            </Routes>
                          </Suspense>
                        </main>
                        
                        {/* Mobile Navigation - Only visible on mobile */}
                        <MobileNavigation />
                        
                        <ScrollToTop />
                        <MobileEnhancer />
                        <Toaster 
                          position="top-center"
                          expand={true}
                          richColors
                          closeButton
                          duration={4000}
                          toastOptions={{
                            style: {
                              background: 'rgba(15, 23, 42, 0.95)',
                              border: '1px solid rgba(59, 130, 246, 0.3)',
                              color: 'white',
                              backdropFilter: 'blur(12px)',
                            },
                          }}
                        />
                      </div>
                    </MobileMenuProvider>
                  </AuthProvider>
                </LoadingProvider>
              </Router>
            </TooltipProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
